import { HttpClient } from '@angular/common/http';
import { Injectable, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { environment } from '@environment';
import {
  UserInfo,
  ConfigValue,
  UserAction,
  WheelSegment,
  MultiWheel,
  MultiWheelTheme,
} from '@longnecktech/splash-commons-fe';
import { GameCampaign } from '@longnecktech/splash-commons-fe/dist/types/campaign';
import { UserActivityService } from '@services/user-activity.service';
import {
  BehaviorSubject,
  map,
  interval,
  filter,
  switchMap,
  Observable,
  mergeMap,
  catchError,
} from 'rxjs';
import { UserActionService } from './user-action.service';
import { Tabs } from '@shared/types/tabs';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private http = inject(HttpClient);
  private userActivityService = inject(UserActivityService);
  private router = inject(Router);
  private userActionService = inject(UserActionService);

  private _isMobile = new BehaviorSubject<boolean>(false);
  private _user = new BehaviorSubject<UserInfo | undefined>(undefined);
  private _game = new BehaviorSubject<MultiWheel | undefined>(undefined);
  private _theme = new BehaviorSubject<MultiWheelTheme | undefined>(undefined);
  private _infoContent = new BehaviorSubject<string | undefined>(undefined);
  private _isMusicMuted = new BehaviorSubject<boolean>(false);
  private _selectedTab = new BehaviorSubject<Tabs>(Tabs.INFO);

  thirdPartyToken = '';
  gameUuid?: string;
  hubGameUuid?: string;
  instance?: string;
  isFromHub = false;
  selectedTab$ = this._selectedTab.asObservable();
  private _flipDone = false;
  readonly user$ = this._user.asObservable();
  readonly isMobile$ = this._isMobile.asObservable();
  readonly game$ = this._game.asObservable();
  readonly gameWithCampaign$ = this.game$.pipe(
    filter((game): game is MultiWheel => !!game?.campaign),
  );
  readonly campaign$ = this.gameWithCampaign$.pipe(
    map((game) => game.campaign as GameCampaign),
  );

  readonly prizes$ = this.game$.pipe(
    filter((game): game is MultiWheel => !!game),
    map((game) => [
      ...(game.outerSegments || []).filter((seg) => !!seg.assetLabel),
      ...(game.innerSegments || []).filter((seg) => !!seg.assetLabel),
    ]),
  );
  readonly uniquePrizes$ = this.game$.pipe(
    filter((game): game is MultiWheel => !!game),
    map((game) => {
      const getUnique = (segments: WheelSegment[]) => {
        const seen = new Set<string>();
        return segments.filter((seg) => {
          const label = seg.assetLabel;
          if (!label || seen.has(label)) return false;
          seen.add(label);
          return true;
        });
      };

      return {
        prizes: getUnique(game.outerSegments || []),
        selections: getUnique(game.innerSegments || []),
      };
    }),
  );

  readonly infoContent$ = this._infoContent.asObservable();
  readonly theme$ = this._theme.asObservable();
  isAnySoundAssetInTheme$ = this.theme$.pipe(
    map((theme) =>
      theme
        ? !!(
            theme.cardSpinSoundAsset ||
            theme.resultPrizeRevealSoundAsset ||
            theme.spinSoundAsset
          )
        : false,
    ),
  );
  readonly thereAnimation$ = this.theme$.pipe(
    map((theme) => theme?.backgroundAnimation),
  );
  readonly isMusicMuted$ = this._isMusicMuted.asObservable();
  private destroyRef = inject(DestroyRef);

  setUser(userInfo: UserInfo): void {
    this._user.next(userInfo);
  }

  setIsMobile(isMobile: boolean): void {
    this._isMobile.next(isMobile);
  }

  setCurrentGame(game: MultiWheel): void {
    const sortedGame: MultiWheel = {
      ...game,
      outerSegments: [...(game.outerSegments || [])].sort(
        (a, b) => a.index - b.index,
      ),
      innerSegments: [...(game.innerSegments || [])].sort(
        (a, b) => a.index - b.index,
      ),
    };
    this._theme.next(sortedGame.theme);
    this._game.next(sortedGame);
    this.gameUuid = sortedGame.uuid;
  }

  getThemeLabels(): ConfigValue[] | undefined {
    return this._theme.value?.labels;
  }

  setInfoContent(type: string | undefined): void {
    this._infoContent.next(type);
  }

  getUserLanguage(): string | undefined {
    return this._user.value?.language;
  }

  getCurrentGame(): MultiWheel | undefined {
    return this._game.value;
  }

  get flipDone(): boolean {
    return this._flipDone;
  }

  toggleMuted(isMuted?: boolean): void {
    this._isMusicMuted.next(isMuted ?? !this._isMusicMuted.value);
  }

  setTab(tab: Tabs): void {
    this._selectedTab.next(tab);
  }

  updateAuthToken(): void {
    // Run every 10 minutes
    interval(10 * 60 * 1000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        // this interval must not refresh the token trigger if the user is not active
        filter(() => !this.userActivityService.isUserInactive()),
        switchMap(() => this.fetchToken()),
      )
      .subscribe({
        next: (user) => {
          this.setUser(user);
        },
        error: () => {
          this.router.navigate(['/error']);
        },
      });
  }

  fetchToken(): Observable<UserInfo> {
    return this.http
      .post<UserInfo>(`${environment.backendUrl}/api/public/auth/token`, {
        token: this.thirdPartyToken,
        instance: this.instance,
      })
      .pipe(
        mergeMap((user) =>
          this.userActionService
            .sendAction(UserAction.GAME_LOGIN, {}, this._game.value?.uuid || '')
            .pipe(map(() => user)),
        ),
        catchError((err) => {
          console.error('Error fetching token:', err);
          throw err;
        }),
      );
  }

  markFlipDone(): void {
    this._flipDone = true;
  }
}
